import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { initAnalytics, pushDL, stopClarity } from "@/lib/analytics";
import { startSessionTelemetry } from "@/lib/sessionTelemetry";
import { sendPageview } from "@/lib/tracking";
import { LanguageProvider, LangUrlSync } from "@/context/LanguageContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { AuthProvider } from "@/context/AuthContext";
import { AdVariantProvider } from "@/context/AdVariantContext";
import PublicLayout from "@/components/layout/PublicLayout";
import ScrollToTop from "@/components/layout/ScrollToTop";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import DataDeletion from "@/pages/DataDeletion";
import { DubaiMarketPage, DubaiCompareAreasPage, MortgageCalculatorPage, UaeMortgagePage, DubaiRecentlyDeliveredPage } from "@/pages/LandingPages";
import AiAgent from "@/pages/AiAgent";
import InsightsPreview from "@/pages/InsightsPreview";
import Insights from "@/pages/Insights";
import InsightsWeekly from "@/pages/InsightsWeekly";
import PropertyDealAnalyzer from "@/pages/PropertyDealAnalyzer";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminForgotPassword from "@/pages/admin/AdminForgotPassword";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLeads from "@/pages/admin/AdminLeadsKanban";
import AdminLeadDetail from "@/pages/admin/AdminLeadDetail";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminStats from "@/pages/admin/AdminStats";
import AdminClickLog from "@/pages/admin/AdminClickLog";
import RequireAuth from "@/components/RequireAuth";
import HostGuard from "@/components/common/HostGuard";

function TrackPageviews() {
  const location = useLocation();
  useEffect(() => {
    const isAdmin = location.pathname.startsWith("/admin");
    // Hard-ban Clarity on CRM — kill the recorder + strip cookies the
    // moment any /admin route is entered, even if Clarity was loaded on
    // a public page earlier in the same SPA session.
    if (isAdmin) {
      stopClarity();
      // Trackers already running in this document can't be fully unloaded:
      // reload so the CRM starts in a clean document where index.html never loads them.
      if (window.__propTrackersLoaded) {
        window.location.replace(window.location.href);
        return;
      }
      // Also sync the <html lang/dir> for admin routes (CRM is EN-only).
      try {
        document.documentElement.lang = "en";
        document.documentElement.dir = "ltr";
      } catch (_e) { /* ignore */ }
    } else {
      sendPageview(location.pathname);
      // GTM SPA pageview – lets History Change / All Pages triggers fire on
      // every client-side route change in both EN and AR.
      pushDL("page_view", { page_path: location.pathname });
      // Mirror the pre-JS lang/dir switch for AR routes so helmet-less
      // navigations still update the attribute.
      // Language/dir is owned by LanguageContext (driven by ?lang=ar); only the
      // legacy /ar prefix is mirrored here. Never force en/ltr on navigation.
      try {
        if (location.pathname.startsWith("/ar")) {
          document.documentElement.lang = "ar";
          document.documentElement.dir = "rtl";
        } else {
          const ar = new URLSearchParams(location.search).get("lang") === "ar";
          document.documentElement.lang = ar ? "ar" : "en";
          document.documentElement.dir = ar ? "rtl" : "ltr";
        }
      } catch (_e) { /* ignore */ }
    }
  }, [location.pathname, location.search]);
  return null;
}

/**
 * Strips <title> nodes from inside every rendered <svg>.
 * Reason: lucide-react (and some Shadcn primitives) embed a <title> for
 * accessibility, but Bing's URL Inspector counts EVERY <title> tag on the
 * page as a page-title, and flags "More than one title tag" as an SEO
 * error. The visual icons still render, only the tooltip-style hint
 * disappears — screen readers get the same information from the icon's
 * parent aria-label anyway. Runs after every route change so newly
 * mounted SVGs are also cleaned.
 */
function ScrubSvgTitles() {
  const location = useLocation();
  useEffect(() => {
    const scrub = () => {
      document.querySelectorAll("svg title").forEach((t) => t.remove());
    };
    // Run once on route change, then again after paint so animated
    // sections (framer-motion delayed mounts) get cleaned too.
    scrub();
    const t1 = setTimeout(scrub, 300);
    const t2 = setTimeout(scrub, 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [location.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    initAnalytics();
    // Boot active-duration + click telemetry. Hooks are idempotent and
    // auto-skip /admin routes (CRM is privacy-protected per Rani).
    startSessionTelemetry();
  }, []);
  return (
    <div className="App grain-overlay">
      <HelmetProvider>
      <LanguageProvider>
        <CurrencyProvider>
          <AuthProvider>
            <BrowserRouter>
              <AdVariantProvider>
              <LangUrlSync />
              <ScrollToTop />
              <TrackPageviews />
              <ScrubSvgTitles />
              {/* HostGuard is placed BEFORE <Routes> so its Helmet renders
                  early, but react-helmet-async's `defer=false` + last-mount
                  semantics still guarantee its noindex/canonical directives
                  override per-page <Helmet> tags on any non-prod host. */}
              <HostGuard />
              <Routes>
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/data-deletion" element={<DataDeletion />} />
                  <Route path="/dubai-market" element={<DubaiMarketPage />} />
                  <Route path="/dubai-market/:slug" element={<DubaiMarketPage />} />
                  <Route path="/dubai-compare-areas" element={<DubaiCompareAreasPage />} />
                  <Route path="/dubai-compare-areas/:pair" element={<DubaiCompareAreasPage />} />
                  <Route path="/mortgage-calculator" element={<MortgageCalculatorPage />} />
                  <Route path="/uae-mortgage" element={<UaeMortgagePage />} />
                  <Route path="/dubai-recently-delivered" element={<DubaiRecentlyDeliveredPage />} />
                  <Route path="/aiagent" element={<AiAgent />} />
                  <Route path="/insights" element={<Insights />} />
                  <Route path="/insights/weekly/:iso_week" element={<InsightsWeekly />} />
                  <Route path="/insights-preview" element={<InsightsPreview />} />
                  <Route path="/property-deal-analyzer" element={<PropertyDealAnalyzer />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                <Route
                  element={
                    <RequireAuth>
                      <AdminLayout />
                    </RequireAuth>
                  }
                >
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/leads" element={<AdminLeads />} />
                  <Route path="/admin/leads/:leadId" element={<AdminLeadDetail />} />
                  <Route path="/admin/stats" element={<AdminStats />} />
                  <Route path="/admin/stats/clicks/:type" element={<AdminClickLog />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                </Route>
              </Routes>
              </AdVariantProvider>
            </BrowserRouter>
            <Toaster
              position="top-center"
              toastOptions={{
                style: {
                  background: "#0D1322",
                  border: "1px solid #1E293B",
                  color: "#fff",
                },
              }}
            />
          </AuthProvider>
        </CurrencyProvider>
      </LanguageProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
